// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ProgressBar.tsx
 * @input Uses React, useId, stylex, color/spacing/radius/transition tokens
 * @output Exports ProgressBar component, ProgressBarProps, ProgressBarVariant types
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/ProgressBar/ProgressBar.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/ProgressBar/ProgressBar.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/ProgressBar/index.ts (exports if types change)
 * - /apps/storybook/stories/ProgressBar.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/ProgressBar/ (showcase blocks)
 */
import { useId } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, fontWeightVars, durationVars, easeVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";

/**
 * Extensible variant map for ProgressBar.
 *
 * Theme packages can add custom variants via TypeScript module augmentation:
 * @example
 * ```
 * declare module '@astryxdesign/core/ProgressBar' {
 *   interface ProgressBarVariantMap {
 *     'brand': true;
 *   }
 * }
 * ```
 */

/**
 * Progress bar variant type — maps to semantic color tokens.
 * Extensible via module augmentation of ProgressBarVariantMap.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Indeterminate animation
// =============================================================================

const indeterminateSlide = "xr3m334-B";

// =============================================================================
// Styles
// =============================================================================

const styles = {
  container: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "xzye2dw",
    kzqmXN: "xh8yej3",
    k7Eaqz: "x900493",
    $$css: true
  },
  fill: {
    kZKoxP: "x5yr21d",
    kaIpWk: "xjspbzw",
    k1ekBW: "xxrbq2n",
    kIyJzY: "x80gvsz",
    kAMwcw: "xlr8y92",
    $$css: true
  },
  indeterminateFill: {
    kZKoxP: "x5yr21d",
    kzqmXN: "xz84dc7",
    kaIpWk: "xjspbzw",
    kKVMdj: "xozk8ic",
    k44tkh: "xmg6eyc xnh0sag",
    kyAemX: "x4hg4is",
    ko0y90: "xa4qsjk",
    $$css: true
  }
};
const variantStyles = {
  accent: {
    kWkggS: "x1ewilqj",
    $$css: true
  },
  success: {
    kWkggS: "xdsz4j9",
    $$css: true
  },
  warning: {
    kWkggS: "x1q8g9m5",
    $$css: true
  },
  error: {
    kWkggS: "x1pjz0fi",
    $$css: true
  },
  neutral: {
    kWkggS: "x16fr6go",
    $$css: true
  },
  disabled: {
    kWkggS: "x16fr6go",
    $$css: true
  }
};
function defaultFormatValueLabel(value, max) {
  const pct = max > 0 ? Math.round(value / max * 100) : 0;
  return `${pct}%`;
}

/**
 * A progress bar for displaying determinate or indeterminate progress.
 *
 * In determinate mode, displays a known value within a range (upload progress,
 * disk usage, etc). In indeterminate mode, shows an animated loading indicator
 * for unknown progress.
 *
 * ProgressBar is intentionally minimal — compose additional labels, status
 * icons, and descriptions alongside the bar using layout components rather
 * than adding props to ProgressBar itself.
 *
 * Styles use Astryx theme tokens via StyleX.
 * Wrap your app in <Theme> to apply a theme.
 *
 * @example
 * ```
 * <ProgressBar value={75} label="Upload progress" />
 * <ProgressBar isIndeterminate label="Loading..." />
 * <ProgressBar value={3.2} max={5} label="Disk usage" hasValueLabel
 *   formatValueLabel={(v, m) => `${v} GB / ${m} GB`} />
 * <ProgressBar value={30} label="Canceled" isDisabled hasValueLabel />
 * ```
 */
export function ProgressBar({
  value = 0,
  max = 100,
  label,
  isLabelHidden = false,
  hasValueLabel = false,
  formatValueLabel = defaultFormatValueLabel,
  variant = 'accent',
  isIndeterminate = false,
  isDisabled = false,
  xstyle,
  className,
  style,
  'data-testid': dataTestId,
  ref,
  ...rest
}) {
  const labelId = useId();
  // A non-finite value or max (e.g. a NaN from an upstream `loaded / total`
  // with total 0) would otherwise leak into aria-valuenow, the value label,
  // and the fill width as the string "NaN". Treat it as empty progress,
  // matching the max=0 handling below.
  const safeValue = Number.isFinite(value) ? value : 0;
  const safeMax = Number.isFinite(max) ? max : 0;
  const clampedValue = Math.min(Math.max(0, safeValue), safeMax);
  const percentage = safeMax > 0 ? clampedValue / safeMax * 100 : 0;
  const valueText = formatValueLabel(clampedValue, safeMax);
  const showValueLabel = hasValueLabel && !isIndeterminate;
  const fillVariant = isDisabled ? 'disabled' : variant;
  return /*#__PURE__*/_jsxs("div", {
    ref: ref,
    ...mergeProps(themeProps('progressbar', {
      variant
    }), stylex.props(styles.container, xstyle), className, style),
    "data-testid": dataTestId,
    ...rest,
    children: [!isLabelHidden || showValueLabel ? /*#__PURE__*/_jsxs("div", {
      ...{
        className: "x78zum5 x1qughib x1pha0wt"
      },
      children: [/*#__PURE__*/_jsx("span", {
        id: labelId,
        ...{
          0: {
            className: "xjm74w1 xw6l6zx x1e4wzip x1tgivj0"
          },
          2: {
            className: "xjm74w1 xw6l6zx x1e4wzip x1tgivj0 x10l6tqk x1i1rx1s xjm9jq1 x1717udv xkdpibf xb3r6kr xzpqnlu xuxw1ft xc342km"
          },
          1: {
            className: "xjm74w1 xw6l6zx x1e4wzip xnbbluu"
          },
          3: {
            className: "xjm74w1 xw6l6zx x1e4wzip x10l6tqk x1i1rx1s xjm9jq1 x1717udv xkdpibf xb3r6kr xzpqnlu xuxw1ft xc342km xnbbluu"
          }
        }[!!isLabelHidden << 1 | !!isDisabled << 0],
        children: label
      }), showValueLabel && /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "xjm74w1 xw6l6zx x1sodnla xv1l7n4"
          },
          1: {
            className: "xjm74w1 xw6l6zx x1sodnla xnbbluu"
          }
        }[!!isDisabled << 0],
        children: valueText
      })]
    }) : /*#__PURE__*/_jsx(VisuallyHidden, {
      id: labelId,
      children: label
    }), /*#__PURE__*/_jsx("div", {
      role: "progressbar",
      "aria-valuenow": isIndeterminate ? undefined : clampedValue,
      "aria-valuemin": isIndeterminate ? undefined : 0,
      "aria-valuemax": isIndeterminate ? undefined : safeMax,
      "aria-labelledby": labelId,
      "aria-valuetext": isIndeterminate ? undefined : valueText,
      ...mergeProps(themeProps('progressbar-track'), {
        className: "xh8yej3 xdk7pt xwmxj5m xjspbzw xb3r6kr"
      }),
      children: isIndeterminate ? /*#__PURE__*/_jsx("div", {
        ...mergeProps(themeProps('progressbar-fill', {
          variant: fillVariant
        }), stylex.props(styles.indeterminateFill, variantStyles[fillVariant]))
      }) : /*#__PURE__*/_jsx("div", {
        ...mergeProps(themeProps('progressbar-fill', {
          variant: fillVariant
        }), stylex.props(styles.fill, variantStyles[fillVariant])),
        style: {
          width: `${percentage}%`
        }
      })
    })]
  });
}
ProgressBar.displayName = 'ProgressBar';